/**
 * Navigation & Background & Mobile Menu - Moon Moon Agency
 * Handles navigation, background filters, and mobile menu interactions
 */

/*********************************************************************************************
 * 🎯 NAVIGATION MODULE
 */
const NavigationManager = {
	// Local configuration
	config: {
		selectors: {
			header: 'header',
			megaMenuLink: '[data-megamenulink]',
			menuLink: '[data-menulink]',
			filter: '[data-filter]',
			cartFilter: '[data-drawer-cart]',
			searchContainer: '[data-search-container]',
			headerBurger: '[data-header-burger]',
			menuMobile: '[data-menu-mobile]',
			menuSublinks: '[data-menu-sublinks]'
		},
		classes: {
			active: 'active',
			open: 'mm-open',
			drawerOpen: 'mm-drawer-open',
			stopScrolling: 'stop-scrolling',
			mobileMenuSlided: 'mm-mobile-menu-elem-slided'
		},
		timing: {
			filterHideDelay: 300,
			megaMenuSwitchDelay: 500
		}
	},

	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	// Hover delay state: locks nav links for 500ms after leaving a mega menu
	_hoverState: {
		closeTimer: null,
		locked: false,
		lastX: 0,
		lastY: 0
	},

	// Same lock system for second-level (product category) links inside a mega menu
	_subLinkState: {
		closeTimer: null,
		locked: false,
		context: null
	},

	/**
	 * Private helper to find the mega menu sibling, skipping auxiliary tags.
	 * STOPS if it encounters another menu link to avoid grabbing the wrong menu.
	 */
	_getMegaMenu(link) {
		if (!link) return null;
		let sibling = link.nextElementSibling;
		while (sibling) {
			// If we found the mega menu, return it
			if (sibling.nodeType === 1 && sibling.hasAttribute('data-megamenu')) {
				return sibling;
			}
			// If we hit another menu link, STOP. This link has no mega menu.
			if (sibling.nodeType === 1 && (sibling.hasAttribute('data-menulink') || sibling.hasAttribute('data-megamenulink') || sibling.tagName === 'A')) {
				return null;
			}
			sibling = sibling.nextElementSibling;
		}
		return null;
	},

	/**
	 * Initialize navigation systems
	 */
	init() {
		// Override timing from theme settings if the data attribute is present
		const headerEl = window.getElement('[data-mm-header]');
		const delay = parseInt(headerEl?.dataset.menuSwitchDelay, 10);
		if (!isNaN(delay)) this.config.timing.megaMenuSwitchDelay = delay;

		this.initMegaMenus();
		this.initBackgroundFilter();
		this.initAccessibilityHandlers();
		this.initMegaMenuAccessibility();
		this.initMobileSubmenuAccessibility();
	},

	/**
	 * Initialize accessibility keyboard handlers for header buttons
	 */
	initAccessibilityHandlers() {
		// Track keyboard events to detect keyboard navigation
		document.addEventListener('keydown', () => {
			MobileMenuManager._wasKeyboardEvent = true;
			// Reset after a short delay (user might switch to mouse/touch)
			setTimeout(() => {
				MobileMenuManager._wasKeyboardEvent = false;
			}, 1000);
		});

		// Track pointer events (mouse/touch) to reset keyboard flag
		document.addEventListener('mousedown', () => {
			MobileMenuManager._wasKeyboardEvent = false;
		});
		document.addEventListener('touchstart', () => {
			MobileMenuManager._wasKeyboardEvent = false;
		});

		// Burger menu button
		const burgerBtn = window.getElement('[data-header-burger]');
		if (burgerBtn) {
			burgerBtn.addEventListener('click', () => {
				window.mmMobileMenu();
				// Update aria-expanded
				const isOpen = burgerBtn.getAttribute('aria-expanded') === 'true';
				burgerBtn.setAttribute('aria-expanded', !isOpen);
			});

			// Handle keyboard activation (Enter/Space)
			burgerBtn.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					MobileMenuManager._wasKeyboardEvent = true;
					burgerBtn.click();
				}
			});
		}

		// Search toggle button
		const searchBtn = window.getElement('[data-search-toggle]');
		if (searchBtn) {
			searchBtn.addEventListener('click', () => {
				window.mmCloseMobileMenu();
				window.mmShowSearch(searchBtn);
				// Update aria-expanded
				const isOpen = searchBtn.getAttribute('aria-expanded') === 'true';
				searchBtn.setAttribute('aria-expanded', !isOpen);
			});
		}

		// Cart toggle button
		const cartBtn = window.getElement('[data-cart-toggle]');
		if (cartBtn) {
			cartBtn.addEventListener('click', () => {
				window.mmOpenCart(cartBtn);
				window.mmCloseMobileMenu(true);
				window.mmHideSearch(true);
			});
		}

		// Global Escape key handler (close all overlays)
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' || e.key === 'Esc') {
				// Let MobileMenuManager handle it if active
				if (MobileMenuManager.focusTrap.isActive) return;

				const mobileMenu = window.getElement(this.config.selectors.menuMobile);
				if (mobileMenu?.classList.contains(this.config.classes.open)) {
					window.mmCloseMobileMenu();
					burgerBtn?.focus(); // Return focus to burger button
				}
			}
		});

		window.log('info', 'NavigationManager', 'Accessibility handlers initialized');
	},

	/**
	 * Initialize mega menu and submenu positioning
	 */
	initMegaMenus() {
		const header = window.getElement(this.config.selectors.header);
		if (!header) {
			window.log('warn', 'NavigationManager', 'Header not found');
			return;
		}

		const headerHeight = header.offsetHeight;

		// Position mega menus (data-megamenulink)
		window.getElements(this.config.selectors.megaMenuLink).forEach((menuElem) => {
			const megaMenu = menuElem.nextElementSibling;
			if (megaMenu?.hasAttribute('data-megamenu')) {
				megaMenu.style.top = `${headerHeight}px`;
				// Simple dropdown: align under the specific link
				if (megaMenu.classList.contains('mm-header-simple-dropdown')) {
					megaMenu.style.left = `${menuElem.offsetLeft}px`;
				}
			}
		});

		// Position submenus (data-menulink with nested data-megamenu)
		window.getElements(this.config.selectors.menuLink).forEach((menuElem) => {
			const submenu = menuElem.querySelector('[data-megamenu]');
			if (submenu) {
				submenu.style.top = `${headerHeight}px`;
			}
		});
	},

	/**
	 * Initialize mega menu keyboard accessibility
	 */
	initMegaMenuAccessibility() {
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const regularMenuLinks = window.getElements(this.config.selectors.menuLink);

		// Track mouse position so we can detect what's under the cursor after unlock
		document.addEventListener('mousemove', (e) => {
			this._hoverState.lastX = e.clientX;
			this._hoverState.lastY = e.clientY;
		}, { passive: true });

		// Mega menu links (with dropdown)
		megaMenuLinks.forEach((link) => {
			const megaMenu = this._getMegaMenu(link);

			if (!megaMenu) {
				window.log('warn', 'NavigationManager', 'Mega menu not found for link', { title: link.textContent.trim() });
				return;
			}

			// Click to toggle mega menu
			link.addEventListener('click', (e) => {
				e.preventDefault();
				this._cancelCloseTimer();
				this.toggleMegaMenu(link, megaMenu);
			});

			// Keyboard: Enter to toggle, Arrow Down to focus first item
			link.addEventListener('keydown', (e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					this._cancelCloseTimer();
					this.toggleMegaMenu(link, megaMenu);
				} else if (e.key === 'ArrowDown' || e.key === ' ') {
					window.log('info', 'NavigationManager', 'ArrowDown or Space pressed on mega menu link');
					e.preventDefault();
					e.stopPropagation();

					const isOpen = link.getAttribute('aria-expanded') === 'true';
					if (!isOpen) {
						this._cancelCloseTimer();
						this.openMegaMenu(link, megaMenu);
					}
					// Activate focus trap when opening via keyboard
					this.setupFocusTrap(megaMenu, link);
				}
			});

			// Hover to open — if returning to the same open link, cancel any pending close timer
			link.addEventListener('mouseenter', () => {
				const alreadyOpen = link.getAttribute('aria-expanded') === 'true';
				if (alreadyOpen) {
					this._cancelCloseTimer();
					return;
				}
				if (this._hoverState.locked) return;
				this._cancelCloseTimer();
				this.closeAllMegaMenus();
				this.openMegaMenu(link, megaMenu);
			});

			// Start close timer when leaving link, unless going directly into its mega menu
			link.addEventListener('mouseleave', (e) => {
				if (megaMenu.contains(e.relatedTarget)) return;
				this._startCloseTimer();
			});

			// Re-entering the mega menu cancels the pending close
			megaMenu.addEventListener('mouseenter', () => {
				this._cancelCloseTimer();
			});

			// Start close timer when leaving mega menu, unless going back to its link
			megaMenu.addEventListener('mouseleave', (e) => {
				if (link.contains(e.relatedTarget)) return;
				this._startCloseTimer();
			});
		});

		// Regular menu links and submenus
		regularMenuLinks.forEach((link) => {
			// Only add listener if it's NOT a mega menu link
			if (!link.hasAttribute('data-megamenulink')) {
				const submenu = link.querySelector('[data-megamenu]') || this._getMegaMenu(link);

				if (submenu && submenu.hasAttribute('data-megamenu')) {
					// This link HAS a submenu - open it on hover and close others
					link.addEventListener('mouseenter', () => {
						const alreadyOpen = link.getAttribute('aria-expanded') === 'true';
						if (alreadyOpen) {
							this._cancelCloseTimer();
							return;
						}
						if (this._hoverState.locked) return;
						this._cancelCloseTimer();
						this.closeAllMegaMenus();
						this.openMegaMenu(link, submenu);
					});

					link.addEventListener('mouseleave', (e) => {
						if (submenu.contains(e.relatedTarget)) return;
						this._startCloseTimer();
					});

					submenu.addEventListener('mouseenter', () => {
						this._cancelCloseTimer();
					});

					submenu.addEventListener('mouseleave', (e) => {
						if (link.contains(e.relatedTarget)) return;
						this._startCloseTimer();
					});
				} else {
					// This link has NO dropdown - close all menus on hover (respects lock)
					link.addEventListener('mouseenter', () => {
						if (this._hoverState.locked) return;
						this.closeAllMegaMenus();
					});
				}
			}
		});

		// Mouse leave header: only close immediately if not in the lock window
		const header = window.getElement(this.config.selectors.header);
		if (header) {
			header.addEventListener('mouseleave', () => {
				if (!this._hoverState.locked) {
					this.closeAllMegaMenus();
				}
				// If locked, the timer will close the menu after the delay
			});
		}

		// Escape key closes all mega menus
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' || e.key === 'Esc') {
				const hasOpenMenu = Array.from(megaMenuLinks).some(
					(link) => link.getAttribute('aria-expanded') === 'true'
				);
				if (hasOpenMenu) {
					this._cancelCloseTimer();
					this.closeAllMegaMenus();
				}
			}
		});

		// Initialize mega menu product links hover/focus
		this.initMegaMenuProductLinks();

		window.log('info', 'NavigationManager', 'Mega menu accessibility initialized');
	},

	/**
	 * Apply or remove pointer-events:none on all nav links so CSS :hover
	 * effects (border animations etc.) are also blocked during the lock.
	 * The mega menus themselves are left untouched so the user can still
	 * interact with the open one.
	 */
	_setNavLinkLock(locked) {
		const allLinks = [
			...window.getElements(this.config.selectors.megaMenuLink),
			...window.getElements(this.config.selectors.menuLink)
		];
		allLinks.forEach(link => {
			link.style.pointerEvents = locked ? 'none' : '';
		});
	},

	/**
	 * Start the 500ms lock timer: blocks other mega menus from opening
	 * until the delay expires, then closes the current menu and checks
	 * whether the cursor is still over a nav link to open.
	 */
	_startCloseTimer() {
		if (this._hoverState.closeTimer) return;
		this._hoverState.locked = true;
		this._setNavLinkLock(true);
		this._hoverState.closeTimer = setTimeout(() => {
			this._hoverState.closeTimer = null;
			this._hoverState.locked = false;
			this._setNavLinkLock(false);
			this.closeAllMegaMenus();
			this._checkHoverAfterUnlock();
		}, this.config.timing.megaMenuSwitchDelay);
	},

	/**
	 * Cancel a pending close timer and immediately release the lock
	 */
	_cancelCloseTimer() {
		if (this._hoverState.closeTimer) {
			clearTimeout(this._hoverState.closeTimer);
			this._hoverState.closeTimer = null;
			this._hoverState.locked = false;
			this._setNavLinkLock(false);
		}
	},

	/**
	 * After the lock expires, check if the cursor landed on a nav link
	 * and open its mega menu if so.
	 */
	_checkHoverAfterUnlock() {
		const { lastX, lastY } = this._hoverState;

		const allMegaLinks = window.getElements(this.config.selectors.megaMenuLink);
		for (const link of allMegaLinks) {
			const rect = link.getBoundingClientRect();
			if (lastX >= rect.left && lastX <= rect.right && lastY >= rect.top && lastY <= rect.bottom) {
				const megaMenu = this._getMegaMenu(link);
				if (megaMenu) this.openMegaMenu(link, megaMenu);
				return;
			}
		}

		const allMenuLinks = window.getElements(this.config.selectors.menuLink);
		for (const link of allMenuLinks) {
			if (!link.hasAttribute('data-megamenulink')) {
				const rect = link.getBoundingClientRect();
				if (lastX >= rect.left && lastX <= rect.right && lastY >= rect.top && lastY <= rect.bottom) {
					const submenu = link.querySelector('[data-megamenu]') || this._getMegaMenu(link);
					if (submenu) this.openMegaMenu(link, submenu);
					return;
				}
			}
		}
	},

	/**
	 * Initialize mega menu product links (replace inline onmouseenter)
	 */
	initMegaMenuProductLinks() {
		const productLinks = window.getElements('[data-mega-menu-products-link]');

		productLinks.forEach((link) => {
			const hideSelector = link.dataset.hideSelector;
			const showSelector = link.dataset.showSelector;

			// Hover: wait for lock to expire before switching
			link.addEventListener('mouseenter', () => {
				if (this._subLinkState.locked) return;
				const context = link.closest('[data-megamenu]');
				this._cancelSubLinkTimer();

				const siblings = link.parentElement.querySelectorAll('[data-mega-menu-products-link]');
				siblings.forEach(s => s.classList.remove('active'));
				link.classList.add('active');

				this.showMegaMenuProducts(hideSelector, showSelector, context);
			});

			// Start lock timer when leaving a product link
			link.addEventListener('mouseleave', () => {
				const context = link.closest('[data-megamenu]');
				this._startSubLinkTimer(context);
			});

			// Focus (keyboard): no delay, always switch immediately
			link.addEventListener('focus', () => {
				this._cancelSubLinkTimer();
				const siblings = link.parentElement.querySelectorAll('[data-mega-menu-products-link]');
				siblings.forEach(s => s.classList.remove('active'));
				link.classList.add('active');
				const context = link.closest('[data-megamenu]');
				this.showMegaMenuProducts(hideSelector, showSelector, context);
			});
		});
	},

	_startSubLinkTimer(context) {
		if (this._subLinkState.closeTimer) return;
		this._subLinkState.locked = true;
		this._subLinkState.context = context;
		context?.querySelectorAll('[data-mega-menu-products-link]').forEach(link => {
			link.style.pointerEvents = 'none';
		});
		this._subLinkState.closeTimer = setTimeout(() => {
			this._subLinkState.closeTimer = null;
			this._subLinkState.locked = false;
			this._subLinkState.context = null;
			context?.querySelectorAll('[data-mega-menu-products-link]').forEach(link => {
				link.style.pointerEvents = '';
			});
			this._checkSubLinkHoverAfterUnlock(context);
		}, this.config.timing.megaMenuSwitchDelay);
	},

	_cancelSubLinkTimer() {
		if (this._subLinkState.closeTimer) {
			clearTimeout(this._subLinkState.closeTimer);
			this._subLinkState.closeTimer = null;
		}
		if (this._subLinkState.locked) {
			this._subLinkState.locked = false;
			this._subLinkState.context?.querySelectorAll('[data-mega-menu-products-link]').forEach(link => {
				link.style.pointerEvents = '';
			});
		}
		this._subLinkState.context = null;
	},

	_checkSubLinkHoverAfterUnlock(context) {
		if (!context) return;
		const { lastX, lastY } = this._hoverState;
		const links = context.querySelectorAll('[data-mega-menu-products-link]');
		for (const link of links) {
			const rect = link.getBoundingClientRect();
			if (lastX >= rect.left && lastX <= rect.right && lastY >= rect.top && lastY <= rect.bottom) {
				const siblings = link.parentElement.querySelectorAll('[data-mega-menu-products-link]');
				siblings.forEach(s => s.classList.remove('active'));
				link.classList.add('active');
				this.showMegaMenuProducts(link.dataset.hideSelector, link.dataset.showSelector, context);
				return;
			}
		}
	},

	/**
	 * Toggle mega menu state
	 */
	toggleMegaMenu(link, megaMenu) {
		const isOpen = link.getAttribute('aria-expanded') === 'true';
		if (isOpen) {
			this.closeMegaMenu(link, megaMenu);
		} else {
			this.closeAllMegaMenus(); // Close others first
			this.openMegaMenu(link, megaMenu);
		}
	},

	/**
	 * Open mega menu
	 */
	openMegaMenu(link, megaMenu) {
		link.setAttribute('aria-expanded', 'true');
		megaMenu.classList.add('mm-open');

		// Reset inner sub-menus to default state (first item active)
		this.resetMegaMenuState(megaMenu);

		this.showBackgroundFilter();
	},

	/**
	 * Reset mega menu sub-navigation to its initial state
	 */
	resetMegaMenuState(megaMenu) {
		const firstProductLink = megaMenu.querySelector('[data-mega-menu-products-link]');
		if (firstProductLink) {
			// Manage active class on links
			const siblings = firstProductLink.parentElement.querySelectorAll('[data-mega-menu-products-link]');
			siblings.forEach((s) => s.classList.remove('active'));
			firstProductLink.classList.add('active');

			// Re-trigger showing the first products group
			const hideSelector = firstProductLink.dataset.hideSelector;
			const showSelector = firstProductLink.dataset.showSelector;
			if (hideSelector && showSelector) {
				this.showMegaMenuProducts(hideSelector, showSelector, megaMenu);
			}
		}
	},

	/**
	 * Close mega menu
	 */
	closeMegaMenu(link, megaMenu) {
		link.setAttribute('aria-expanded', 'false');
		megaMenu.classList.remove('mm-open');
	},

	/**
	 * Close all mega menus
	 */
	closeAllMegaMenus() {
		this._cancelSubLinkTimer();

		// Close mega menu links
		const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		allMegaMenuLinks.forEach((link) => {
			link.setAttribute('aria-expanded', 'false');
			const megaMenu = this._getMegaMenu(link);
			if (megaMenu) {
				megaMenu.classList.remove('mm-open');
			}
		});

		// Remove focus trap if active
		this.removeFocusTrap();

		// Close regular menu links
		const allRegularMenuLinks = window.getElements(this.config.selectors.menuLink);
		allRegularMenuLinks.forEach((link) => {
			link.setAttribute('aria-expanded', 'false');
			const submenu = link.querySelector('[data-megamenu]') || this._getMegaMenu(link);
			if (submenu) {
				submenu.classList.remove('mm-open');
			}
		});

		this.hideBackgroundFilter();
	},

	/**
	 * Show specific mega menu products
	 */
	showMegaMenuProducts(classElemsToHide, classElemToShow, context = document) {
		try {
			const container = context || document;
			// Fade out all siblings
			container.querySelectorAll(classElemsToHide).forEach((elem) => {
				if (classElemToShow && elem.matches(classElemToShow)) return;
				elem.classList.remove('mm-childchild-visible');
				elem.style.display = '';
			});

			// Fade in target element
			const elemToShow = container.querySelector(classElemToShow);
			if (elemToShow) {
				elemToShow.style.display = '';
				elemToShow.classList.add('mm-childchild-visible');
			}
			window.emit('global:mega-menu-products-switched', { hideSelector: classElemsToHide, showSelector: classElemToShow });
		} catch (error) {
			window.log('error', 'NavigationManager', 'Failed to switch mega menu products', { error: error.message });
		}
	},

	/**
	 * Setup focus trap for mega menu
	 */
	setupFocusTrap(container, triggerElement) {
		this.focusTrap.triggerElement = triggerElement;

		// Handle keydown for trap and arrow navigation
		this.focusTrap.handleKeyDown = (e) => {
			// Get ALL potentially focusable elements
			const allFocusable = Array.from(container.querySelectorAll(
				'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
			));

			// Filter only visible ones (crucial because product groups are toggled)
			const focusableElements = allFocusable.filter(el => {
				return el.offsetWidth > 0 && el.offsetHeight > 0 && el.tabIndex !== -1;
			});

			if (focusableElements.length === 0) return;

			this.focusTrap.firstFocusable = focusableElements[0];
			this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

			// Escape closes all mega menus
			if (e.key === 'Escape') {
				e.preventDefault();
				this.closeAllMegaMenus();
				this.focusTrap.triggerElement?.focus();
				return;
			}

			// Tab trap (using dynamic visible list)
			if (e.key === 'Tab') {
				if (e.shiftKey) {
					if (document.activeElement === this.focusTrap.firstFocusable) {
						e.preventDefault();
						this.focusTrap.lastFocusable?.focus();
					}
				} else {
					if (document.activeElement === this.focusTrap.lastFocusable) {
						e.preventDefault();
						this.focusTrap.firstFocusable?.focus();
					}
				}
				return;
			}

			// Arrow keys & Space navigation
			if (['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', ' '].includes(e.key)) {
				e.preventDefault();
				const current = document.activeElement;
				const index = focusableElements.indexOf(current);
				
				let nextIndex = index;

				if (e.key === 'ArrowDown') {
					nextIndex = (index + 1) % focusableElements.length;
				} else if (e.key === 'ArrowUp') {
					nextIndex = (index - 1 + focusableElements.length) % focusableElements.length;
				} else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === ' ') {
					// Column-based navigation logic
					const currentRect = current.getBoundingClientRect();
					const isForward = e.key === 'ArrowRight' || e.key === ' ';
					
					// Find elements in the "next" or "previous" column area
					const candidates = focusableElements.filter(el => {
						const rect = el.getBoundingClientRect();
						if (isForward) {
							return rect.left > currentRect.right - 10; // Slightly left to account for padding
						} else {
							return rect.right < currentRect.left + 10;
						}
					});

					if (candidates.length > 0) {
						// Find the one closest to current Y level
						candidates.sort((a, b) => {
							const distA = Math.abs(a.getBoundingClientRect().top - currentRect.top);
							const distB = Math.abs(b.getBoundingClientRect().top - currentRect.top);
							return distA - distB;
						});
						nextIndex = focusableElements.indexOf(candidates[0]);
					} else if (isForward) {
						// Loop to first if at hard right
						nextIndex = (index + 1) % focusableElements.length;
					} else {
						// Loop to last if at hard left
						nextIndex = (index - 1 + focusableElements.length) % focusableElements.length;
					}
				}

				focusableElements[nextIndex]?.focus();
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		const allFocusable = Array.from(container.querySelectorAll(
			'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
		));
		const initialFocus = allFocusable.find(el => el.offsetWidth > 0 && el.offsetHeight > 0);
		initialFocus?.focus();

		window.log('info', 'NavigationManager', 'Mega menu focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			// We don't automatically focus trigger here as closeAllMegaMenus might be called by hover elsewhere
			window.log('info', 'NavigationManager', 'Mega menu focus trap removed');
		}
	},

	/**
	 * Initialize background filter hover behaviors
	 */
	initBackgroundFilter() {
		const elements = {
			header: window.getElement(this.config.selectors.header),
			searchContainer: window.getElement(this.config.selectors.searchContainer),
			filter: window.getElement(this.config.selectors.filter),
			cart: window.getElement(this.config.selectors.cartFilter)
		};

		if (!elements.header || !elements.filter || !elements.cart) {
			window.log('warn', 'NavigationManager', 'Background filter elements not found');
			return;
		}

		const { header, searchContainer, filter, cart } = elements;
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const menuLinks = window.getElements(this.config.selectors.menuLink);

		const toggleFilter = (show) => {
			const mobileMenu = window.getElement(this.config.selectors.menuMobile);
			const isMobileMenuOpen = mobileMenu?.classList.contains(this.config.classes.open);

			// If we are trying to hide the filter, but the mobile menu is open, DON'T hide it.
			if (!show && isMobileMenuOpen) return;

			filter.classList.toggle(this.config.classes.active, show);
			window.emit(`global:background-filter-${show ? 'activated' : 'deactivated'}`, { 
				trigger: show ? 'toggle-on' : 'toggle-off',
				source: 'navigation' 
			});
		};

		// Event listeners
		megaMenuLinks.forEach((link) => 
			link.addEventListener('mouseenter', () => toggleFilter(true))
		);
		
		menuLinks.forEach((link) => {
			// Mega menu links show the filter — skip them here so they don't immediately hide it
			if (link.hasAttribute('data-megamenulink')) return;
			link.addEventListener('mouseenter', () => toggleFilter(false));
		});

		header.addEventListener('mouseleave', () => {
			const mobileMenu = window.getElement(this.config.selectors.menuMobile);
			const searchOpen = searchContainer?.classList.contains(this.config.classes.open);
			const cartOpen = cart.classList.contains(this.config.classes.drawerOpen);
			const mobileMenuOpen = mobileMenu?.classList.contains(this.config.classes.open);

			if (!searchOpen && !cartOpen && !mobileMenuOpen && !this._hoverState.locked) {
				toggleFilter(false);
			}
		});

		// Click on filter to close all overlays
		filter.addEventListener('click', () => {
			window.mmCloseMobileMenu();
			window.mmHideSearch?.();
			this.closeAllMegaMenus();
		});

	},

	/**
	 * Show background filter
	 */
	showBackgroundFilter(zIndex) {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) {
			window.log('warn', 'NavigationManager', 'Background filter element not found');
			return;
		}
		
		filter.classList.add(this.config.classes.active);
		if (zIndex) filter.style.zIndex = zIndex;
		window.emit('global:background-filter-activated', { 
			trigger: 'show', 
			zIndex, 
			hasZIndex: !!zIndex 
		});
	},

	/**
	 * Hide background filter
	 */
	hideBackgroundFilter() {
		const mobileMenu = window.getElement(this.config.selectors.menuMobile);
		const isMobileMenuOpen = mobileMenu?.classList.contains(this.config.classes.open);

		// If the mobile menu is open, we don't want to hide the filter via this method
		// (unless specifically called during menu close where the class is removed first)
		if (isMobileMenuOpen) return;

		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) return;
		
		setTimeout(() => filter.style.removeProperty('z-index'), this.config.timing.filterHideDelay);
		filter.classList.remove(this.config.classes.active);
		window.emit('global:background-filter-deactivated', { 
			trigger: 'hide',
			delay: this.config.timing.filterHideDelay 
		});
	},

	/**
	 * Initialize mobile submenu accessibility (replace inline onclick)
	 */
	initMobileSubmenuAccessibility() {
		// Submenu trigger buttons (show next)
		const submenuTriggers = window.getElements('[data-mobile-submenu-trigger]');
		submenuTriggers.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuTrigger;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowNext(targetSelector, btn);
				btn.setAttribute('aria-expanded', 'true');
			});
		});

		// Submenu back buttons (show prev)
		const submenuBackBtns = window.getElements('[data-mobile-submenu-back]');
		submenuBackBtns.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuBack;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowPrev(targetSelector);
				// Find corresponding trigger and update aria-expanded
				const trigger = window.getElement(`[data-mobile-submenu-trigger="${targetSelector}"]`);
				if (trigger) trigger.setAttribute('aria-expanded', 'false');
			});
		});

		// Mobile childlinks triggers (primary menu links with submenus)
		const childlinksTriggers = window.getElements('[data-mobile-childlinks-trigger]');
		childlinksTriggers.forEach((link) => {
			const childlinksId = link.dataset.mobileChildlinksTrigger;
			
			link.addEventListener('click', (e) => {
				// Empêcher la navigation par défaut
				e.preventDefault();
				
				// Trouver le sous-menu associé
				const childlinks = window.getElement(`#${childlinksId}`);
				if (childlinks) {
					// Ouvrir le sous-menu en ajoutant la classe mm-open
					childlinks.classList.add(this.config.classes.open);
					link.setAttribute('aria-expanded', 'true');
					
					// Focus trap for submenu (only auto-focus if using keyboard)
					const shouldFocus = MobileMenuManager._wasKeyboardEvent;
					MobileMenuManager.setupFocusTrap(childlinks, link, shouldFocus);
					
					window.log('info', 'NavigationManager', 'Mobile childlinks opened', { id: childlinksId });
				}
			});
		});

		// Mobile childlinks back buttons (close submenu)
		const childlinksBackBtns = window.getElements('[data-mobile-childlinks-back]');
		childlinksBackBtns.forEach((btn) => {
			btn.addEventListener('click', () => {
				// Trouver le sous-menu parent
				const childlinks = btn.closest('.mm-menu-mobile-childlinks');
				if (childlinks) {
					// Fermer le sous-menu en retirant la classe mm-open
					childlinks.classList.remove(this.config.classes.open);
					
					// Trouver le lien trigger correspondant et mettre à jour aria-expanded
					const childlinksId = childlinks.id;
					const trigger = window.getElement(`[data-mobile-childlinks-trigger="${childlinksId}"]`);
					if (trigger) {
						trigger.setAttribute('aria-expanded', 'false');
					}
					
					// Return focus and restore previous trap
					MobileMenuManager.popFocusTrap();
					
					window.log('info', 'NavigationManager', 'Mobile childlinks closed', { id: childlinksId });
				}
			});
		});

		// Mobile close buttons (close entire menu)
		const closeBtns = window.getElements('[data-mobile-close]');
		closeBtns.forEach((btn) => {
			btn.addEventListener('click', () => {
				window.mmCloseMobileMenu();
			});
		});

		// Initialize positioning based on screen size
		this.updateMobileSubmenuPosition();

		window.log('info', 'NavigationManager', 'Mobile submenu accessibility initialized');
	},

	/**
	 * Update mobile submenu positions based on screen width
	 * < 500px: Move to body (overlay/drawer behavior)
	 * >= 500px: Move back to original parent
	 */
	updateMobileSubmenuPosition() {
		const isSmallScreen = window.innerWidth < 500;
		const submenus = document.querySelectorAll('.mm-menu-mobile-childlinks');

		submenus.forEach(submenu => {
			if (isSmallScreen) {
				// Move to body if not already there
				if (submenu.parentNode !== document.body) {
					// Create placeholder to remember position
					const placeholder = document.createElement('div');
					placeholder.id = `should-be-${submenu.id}`;
					placeholder.style.display = 'none';
					submenu.parentNode.insertBefore(placeholder, submenu);
					document.body.appendChild(submenu);
				}
			} else {
				// Move back to original position if in body
				const placeholder = document.getElementById(`should-be-${submenu.id}`);
				if (placeholder) {
					placeholder.parentNode.insertBefore(submenu, placeholder);
					placeholder.remove();
				}
			}
		});
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		this.initMegaMenus();
		this.updateMobileSubmenuPosition();

		// Close mobile menu when resizing to desktop
		if (window.innerWidth > 1080) {
			const mobileMenu = window.getElement(MobileMenuManager.config.selectors.menuMobile);
			if (mobileMenu?.classList.contains(MobileMenuManager.config.classes.open)) {
				MobileMenuManager.close();
			}
		}
	}
};

/*********************************************************************************************
 * 📱 MOBILE MENU MODULE
 */
const MobileMenuManager = {
	// Reference to NavigationManager config
	config: NavigationManager.config,

	// Focus trap state
	focusTrap: {
		isActive: false,
		stack: [], // Stack of { container, trigger, shouldFocus }
		handleKeyDown: null
	},

	// Track if user is using keyboard navigation
	_wasKeyboardEvent: false,

	// iOS scroll position management
	_scrollPosition: 0,

	/**
	 * Setup focus trap for mobile menu/submenu
	 * @param {HTMLElement} container - The element to trap focus within
	 * @param {HTMLElement} trigger - The element that triggered this trap (to return focus to)
	 * @param {boolean} shouldFocus - Whether to automatically focus first element (only for keyboard navigation)
	 */
	setupFocusTrap(container, trigger = null, shouldFocus = false) {
		// Remove existing listener if active
		if (this.focusTrap.isActive) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
		}

		// Push to stack
		this.focusTrap.stack.push({ container, trigger, shouldFocus });

		// Apply the trap
		this._applyFocusTrap();
	},

	/**
	 * Get all focusable elements within a container, filtering for visibility
	 * and ensuring we don't pick up elements inside closed submenus.
	 */
	_getFocusableElements(container) {
		const allFocusable = Array.from(container.querySelectorAll(
			'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([type="hidden"]), select, textarea'
		));

		return allFocusable.filter(el => {
			// Basic visibility check
			const rect = el.getBoundingClientRect();
			const isVisible = rect.width > 0 && rect.height > 0 && el.tabIndex !== -1;
			if (!isVisible) return false;

			// Check computed styles for display/visibility/opacity
			const style = window.getComputedStyle(el);
			if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;

			// Check if element is inside a childlink/submenu
			const childlinkContainer = el.closest('.mm-menu-mobile-childlinks');
			
			if (childlinkContainer) {
				// If we are currently trapping a DIFFERENT container, or if this childlink isn't open, ignore it
				if (childlinkContainer !== container || !childlinkContainer.classList.contains('mm-open')) {
					return false;
				}
			}

			// Check if inside a closed dropdown (accordion)
			const dropdownContent = el.closest('[data-dropdown-content]');
			if (dropdownContent) {
				const trigger = dropdownContent.previousElementSibling;
				if (trigger && trigger.hasAttribute('data-dropdown-trigger') && !trigger.classList.contains('mm-active')) {
					return false;
				}
			}

			// If the main menu itself is closed (shouldn't happen if we're here, but for safety)
			const mobileMenu = el.closest('.mm-mobile-menu');
			if (mobileMenu && !mobileMenu.classList.contains('mm-open')) {
				return false;
			}

			return true;
		});
	},

	/**
	 * Apply the focus trap for the current top of the stack
	 */
	_applyFocusTrap() {
		const top = this.focusTrap.stack[this.focusTrap.stack.length - 1];
		if (!top) {
			this.focusTrap.isActive = false;
			return;
		}

		const { container, shouldFocus } = top;

		this.focusTrap.handleKeyDown = (e) => {
			const focusableElements = this._getFocusableElements(container);
			if (focusableElements.length === 0) return;

			const firstFocusable = focusableElements[0];
			const lastFocusable = focusableElements[focusableElements.length - 1];

			if (e.key === 'Tab') {
				e.preventDefault(); // Take total control of Tab navigation
				const index = focusableElements.indexOf(document.activeElement);
				
				if (e.shiftKey) {
					// Shift + Tab: going backwards
					if (index <= 0) {
						lastFocusable?.focus();
					} else {
						focusableElements[index - 1].focus();
					}
				} else {
					// Tab: going forwards
					if (index === -1 || index >= focusableElements.length - 1) {
						firstFocusable?.focus();
					} else {
						focusableElements[index + 1].focus();
					}
				}
			} else if (e.key === 'Escape') {
				e.preventDefault();
				e.stopImmediatePropagation();
				
				// If we are in a submenu, go back
				if (container.classList.contains('mm-menu-mobile-childlinks')) {
					const backBtn = container.querySelector('[data-mobile-childlinks-back]');
					if (backBtn) {
						backBtn.click();
					} else {
						this.popFocusTrap();
					}
				} else {
					// Main menu
					window.mmCloseMobileMenu();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Initial focus: only if opened via keyboard (shouldFocus = true)
		// On mobile/touch devices, we don't want to show focus ring when opening via tap
		if (shouldFocus) {
			setTimeout(() => {
				// If this is the main mobile menu, focus on first element of primary menu instead of search input
				let targetElement = null;
				if (container.classList.contains('mm-mobile-menu')) {
					const primaryMenu = container.querySelector('.mm-menu-mobile-menu-primary');
					if (primaryMenu) {
						const primaryMenuFocusable = this._getFocusableElements(primaryMenu);
						if (primaryMenuFocusable.length > 0) {
							targetElement = primaryMenuFocusable[0];
						}
					}
				}
				
				// Fallback to first element of container if no primary menu element found
				if (!targetElement) {
					const focusableElements = this._getFocusableElements(container);
					if (focusableElements.length > 0) {
						targetElement = focusableElements[0];
					}
				}
				
				if (targetElement) {
					targetElement.focus();
					window.log('info', 'MobileMenuManager', 'Focus moved to first element', { element: targetElement.tagName, isPrimaryMenu: container.classList.contains('mm-mobile-menu') });
				}
			}, 50);
		}

		window.log('info', 'MobileMenuManager', 'Focus trap applied', { container: container.id || 'main' });
	},

	/**
	 * Pop the current focus trap and return focus to the trigger
	 */
	popFocusTrap() {
		if (!this.focusTrap.isActive) return;

		document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
		const popped = this.focusTrap.stack.pop();

		if (popped && popped.trigger) {
			popped.trigger.focus();
		}

		if (this.focusTrap.stack.length > 0) {
			this._applyFocusTrap();
		} else {
			this.focusTrap.isActive = false;
		}
		
		window.log('info', 'MobileMenuManager', 'Focus trap popped');
	},

	/**
	 * Remove focus trap and clear stack
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			
			// Return focus to the original trigger (usually the burger button)
			if (this.focusTrap.stack.length > 0 && this.focusTrap.stack[0].trigger) {
				this.focusTrap.stack[0].trigger.focus();
			}

			this.focusTrap.stack = [];
			this.focusTrap.isActive = false;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'MobileMenuManager', 'Focus traps removed');
		}
	},

	/**
	 * Hide/show announcement bar for mobile menu
	 */
	_toggleAnnouncementBar(hide) {
		if (window.innerWidth > 768) return; // Only on mobile

		const announcementBar = document.querySelector('.mm-section-announcement-bar');
		if (!announcementBar) return;

		// La barre reste toujours visible — le header glisse par-dessus (pas de layout shift)
		if (hide || !hide) return;
	},

	/**
	 * Toggle header position for mobile menu
	 * Uses transform:translateY (not top) so it works even at scroll=0 on sticky elements
	 */
	_toggleHeaderPosition(hide) {
		if (window.innerWidth > 768) return; // Only on mobile

		const headerSection = document.querySelector('.mm-section-header');
		if (!headerSection) return;

		if (hide) {
			// Instant slide up : no transition, header covers announcement bar
			headerSection.classList.add('mm-header-menu-open');
		} else {
			// Simultaneous with menu close : CSS transition:transform 180ms takes over
			headerSection.classList.remove('mm-header-menu-open');
		}
	},

	/**
	 * Toggle mobile menu
	 */
	toggle() {
		if (window.innerWidth > 1080) return;

		const elements = {
			header: window.getElement('header'),
			icon: window.getElement(this.config.selectors.headerBurger),
			mobileMenu: window.getElement(this.config.selectors.menuMobile),
			headerMenu: window.getElement(this.config.selectors.header),
			html: document.documentElement
		};

		const mobileMenuSecondary = window.getElement(this.config.selectors.menuSublinks);

		if (Object.values(elements).some((el) => !el)) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found');
			return;
		}

		const { header, icon, mobileMenu, headerMenu, html } = elements;

		const headerHeight = headerMenu.offsetHeight;
		const headerMenuTop = headerMenu.getBoundingClientRect().top;

		// En dessous de 768px : top = hauteur du header (header glisse par transform sur la barre)
		// Au-dessus : hauteur du header + sa position (comportement initial)
		const topPosition =
			window.innerWidth <= 768
				? `${headerHeight}px`
				: `${headerHeight + headerMenuTop}px`;
		const heightCalc = `calc(var(--doc-height) - ${topPosition})`;

		[header, icon, mobileMenu].forEach((el) => el.classList.toggle(this.config.classes.open));

		Object.assign(mobileMenu.style, { top: topPosition, height: heightCalc });
		if (mobileMenuSecondary) mobileMenuSecondary.style.height = heightCalc;

		// Setup focus trap and background filter when menu opens
		mobileMenu.setAttribute('aria-hidden', mobileMenu.classList.contains(this.config.classes.open) ? 'false' : 'true');

		if (mobileMenu.classList.contains(this.config.classes.open)) {
			// iOS Safari : sauvegarder la position du scroll avant de bloquer
			this._scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
			
			// html.classList.add(this.config.classes.stopScrolling);
			// iOS Safari : bloquer aussi le body
			document.body.classList.add(this.config.classes.stopScrolling);
			// iOS Safari : appliquer la position fixe avec l'offset sauvegardé
			if (this._scrollPosition > 0) {
				html.style.top = `-${this._scrollPosition}px`;
				document.body.style.top = `-${this._scrollPosition}px`;
			}
			
			// Use z-index lower than mobile menu (menu is 10, filter should be 5)
			NavigationManager.showBackgroundFilter('3');
			// Stop Lenis if active
			window.LenisManager?.stop();
			// Only auto-focus if opened via keyboard navigation
			const shouldFocus = this._wasKeyboardEvent;
			this.setupFocusTrap(mobileMenu, icon, shouldFocus);
			this._toggleHeaderPosition(true);
		} else {
			// iOS Safari : restaurer la position du scroll après déblocage
			// html.classList.remove(this.config.classes.stopScrolling);
			document.body.classList.remove(this.config.classes.stopScrolling);
			html.style.top = '';
			document.body.style.top = '';
			if (this._scrollPosition > 0) {
				window.scrollTo(0, this._scrollPosition);
				this._scrollPosition = 0;
			}
			
			NavigationManager.hideBackgroundFilter();
			window.LenisManager?.resume();
			this.removeFocusTrap();
			// Fermeture : menu slide + header glisse back simultanément
			mobileMenu.classList.add('mm-closing');
			setTimeout(() => mobileMenu.classList.remove('mm-closing'), 150);
			this._toggleHeaderPosition(false);
		}

		window.emit('global:mobile-menu-toggled', { headerHeight, topPosition });
	},

	/**
	 * Close mobile menu
	 */
	close(keepFilterActive = false) {

		const elements = {
			header: window.getElement('header'),
			icon: window.getElement(this.config.selectors.headerBurger),
			mobileMenu: window.getElement(this.config.selectors.menuMobile),
			html: document.documentElement
		};

		if (Object.values(elements).some((el) => !el)) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found for closing');
			return;
		}

		const { header, icon, mobileMenu, html } = elements;

		[header, icon, mobileMenu].forEach((el) => el.classList.remove(this.config.classes.open));
		mobileMenu.setAttribute('aria-hidden', 'true');

		// iOS Safari : restaurer la position du scroll après déblocage
		// html.classList.remove(this.config.classes.stopScrolling);
		document.body.classList.remove(this.config.classes.stopScrolling);
		html.style.top = '';
		document.body.style.top = '';
		if (this._scrollPosition > 0) {
			window.scrollTo(0, this._scrollPosition);
			this._scrollPosition = 0;
		}

		// Close all open childlinks (submenus)
		const openChildlinks = document.querySelectorAll('.mm-menu-mobile-childlinks.mm-open');
		openChildlinks.forEach(child => {
			child.classList.remove(this.config.classes.open);
			// Reset aria-expanded on trigger if possible
			const id = child.id;
			if (id) {
				const trigger = document.querySelector(`[data-mobile-childlinks-trigger="${id}"]`);
				if (trigger) trigger.setAttribute('aria-expanded', 'false');
			}
		});

		// Remove focus trap when menu closes
		this.removeFocusTrap();

		if (!keepFilterActive) {
			NavigationManager.hideBackgroundFilter();
			window.LenisManager?.resume();
		}
		
		// Show announcement bar and reset header position after menu closes (with delay to match transition)
		this._toggleAnnouncementBar(false);
		this._toggleHeaderPosition(false);
		
		window.emit('global:mobile-menu-closed', { keepFilterActive });
	},

	/**
	 * Show next sub-menu
	 */
	showNext(elemToShowClass, trigger = null) {
		const element = window.getElement(elemToShowClass, false);
		if (element) {
			element.classList.add(this.config.classes.mobileMenuSlided);
			if (trigger) {
				// Only auto-focus if using keyboard navigation
				const shouldFocus = this._wasKeyboardEvent;
				this.setupFocusTrap(element, trigger, shouldFocus);
			}
			window.emit('global:mobile-submenu-next', { selector: elemToShowClass });
		}
	},

	/**
	 * Show previous sub-menu
	 */
	showPrev(elemToShowClass) {
		const element = window.getElement(elemToShowClass, false);
		if (element) {
			element.classList.remove(this.config.classes.mobileMenuSlided);
			this.popFocusTrap();
			window.emit('global:mobile-submenu-prev', { selector: elemToShowClass });
		}
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.NavigationManager = NavigationManager;
window.MobileMenuManager = MobileMenuManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
// Navigation functions
window.getMegaMenuPosition = () => NavigationManager.initMegaMenus();
window.mmMegaMenuShowProducts = (hide, show) => NavigationManager.showMegaMenuProducts(hide, show);

// Background filter functions
window.showBgFilter = (zIndex) => NavigationManager.showBackgroundFilter(zIndex);
window.hideBgFilter = () => NavigationManager.hideBackgroundFilter();

// Mobile menu functions
window.mmMobileMenu = () => MobileMenuManager.toggle();
window.mmCloseMobileMenu = (keep) => MobileMenuManager.close(keep);
window.mmMobileMenuShowNext = (elem, trigger) => MobileMenuManager.showNext(elem, trigger);
window.mmMobileMenuShowPrev = (elem) => MobileMenuManager.showPrev(elem);

/*********************************************************************************************
 * 📱 PREVENT ZOOM ON INPUTS (iOS Safari)
 * Empêche le zoom automatique sur les inputs de newsletter dans le menu mobile
 */
const preventInputZoom = {
	viewportMeta: null,
	originalContent: null,

	init() {
		// Trouver le meta viewport
		this.viewportMeta = document.querySelector('meta[name="viewport"]');
		if (!this.viewportMeta) return;

		// Sauvegarder le contenu original
		this.originalContent = this.viewportMeta.getAttribute('content') || 'width=device-width,initial-scale=1';

		// Détecter les inputs de newsletter dans le menu mobile
		const newsletterInputs = document.querySelectorAll('.mm-newsletter-form input[type="email"]');
		
		newsletterInputs.forEach(input => {
			// Au focus : désactiver le zoom
			input.addEventListener('focus', () => {
				this.disableZoom();
			});

			// Au blur : réactiver le zoom
			input.addEventListener('blur', () => {
				this.enableZoom();
			});
		});

		window.log('info', 'PreventInputZoom', 'Initialized for newsletter inputs');
	},

	disableZoom() {
		if (!this.viewportMeta) return;
		// Désactiver le zoom en ajoutant user-scalable=no et maximum-scale=1
		this.viewportMeta.setAttribute('content', 'width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no');
	},

	enableZoom() {
		if (!this.viewportMeta || !this.originalContent) return;
		// Restaurer le viewport original après un court délai pour éviter les problèmes
		setTimeout(() => {
			this.viewportMeta.setAttribute('content', this.originalContent);
		}, 100);
	}
};

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	NavigationManager.init();
	// Initialiser la prévention du zoom sur les inputs
	preventInputZoom.init();
});

window.addEventListener('resize', () => {
	NavigationManager.onResize();
});